<template>
  <div class="commandes-page">
    <div class="page-header">
      <div class="header-content">
        <h1>
          <i class="fas fa-shopping-cart"></i>
          Commandes
        </h1>
        <p>Gestion et validation des commandes extraites automatiquement</p>
      </div>
      <div class="header-actions">
        <div class="search-box">
          <i class="fas fa-search"></i>
          <input 
            v-model="searchQuery" 
            type="text" 
            placeholder="Rechercher une commande..."
            class="search-input"
          >
        </div>
        <select v-model="statusFilter" class="status-filter">
          <option value="">Tous les statuts</option>
          <option value="DRAFT">Commande en brouillon nécessitant validation</option>
          <option value="PROCESSING">Commande en cours de traitement</option>
          <option value="SENT_TO_SAP">Commande envoyée vers SAP</option>
          <option value="PROCESSED">Commande traitée avec succès</option>
          <option value="PARTIALLY_CLOSED">Commande partiellement clôturée</option>
          <option value="CLOSED">Commande entièrement clôturée</option>
          <option value="ANOMALY">Commande avec anomalie détectée</option>
        </select>
      </div>
    </div>

    <div class="stats-cards">
      <div class="stat-card">
        <div class="stat-icon draft">
          <i class="fas fa-edit"></i>
        </div>
        <div class="stat-content">
          <h3>{{ formatCount(stats.DRAFT) }}</h3>
          <p>Brouillons</p>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon processing">
          <i class="fas fa-cog"></i>
        </div>
        <div class="stat-content">
          <h3>{{ formatCount(stats.PROCESSING) }}</h3>
          <p>En traitement</p>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon processed">
          <i class="fas fa-check-circle"></i>
        </div>
        <div class="stat-content">
          <h3>{{ formatCount(stats.PROCESSED) }}</h3>
          <p>Traitées</p>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon anomaly">
          <i class="fas fa-exclamation-triangle"></i>
        </div>
        <div class="stat-content">
          <h3>{{ formatCount(stats.ANOMALY) }}</h3>
          <p>Anomalies</p>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon total">
          <i class="fas fa-chart-bar"></i>
        </div>
        <div class="stat-content">
          <h3>{{ formatCount(stats.total) }}</h3>
          <p>Total</p>
        </div>
      </div>
    </div>

    <div class="commandes-table">
      <div class="table-header">
        <h2>Liste des commandes</h2>
        <div class="table-actions">
          <button class="btn btn-outline" @click="refreshCommandes">
            <i class="fas fa-sync" :class="{ 'fa-spin': loading }"></i>
            Actualiser
          </button>
        </div>
      </div>

      <div class="table-container">
        <table class="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Client</th>
              <th>Date</th>
              <th>Montant</th>
              <th>Articles</th>
              <th>Statut</th>
              <th>Confiance IA</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="commande in filteredCommandes" :key="commande.id" class="table-row">
              <td class="commande-id" data-label="ID">
                <span class="id-badge">#{{ commande.id }}</span>
              </td>
              <td class="client-info" data-label="Client">
                <div class="client-name">{{ commande.clientName }}</div>
                <div class="client-email">{{ commande.clientEmail || '—' }}</div>
              </td>
              <td class="date-info" data-label="Date">
                <div class="date">{{ formatDate(commande.createdAt) }}</div>
                <div class="time">{{ formatTime(commande.createdAt) }}</div>
              </td>
              <td class="amount" data-label="Montant">
                <span class="currency">{{ formatCurrency(commande.totalAmount) }}</span>
              </td>
              <td class="items-count" data-label="Articles">
                <span class="badge">{{ formatCount(commande.itemsCount) }} articles</span>
              </td>
              <td class="status" data-label="Statut">
                <span class="status-badge" :class="getStatusClass(commande.status)">
                  {{ getStatusText(commande.status) }}
                </span>
              </td>
              <td class="confidence" data-label="Confiance IA">
                <div class="confidence-bar">
                  <div 
                    class="confidence-fill" 
                    :style="{ width: (commande.aiConfidence ?? 0) + '%' }"
                    :class="getConfidenceClass(commande.aiConfidence)"
                  ></div>
                  <span class="confidence-text">{{ formatPercent(commande.aiConfidence) }}</span>
                </div>
              </td>
              <td class="actions" data-label="Actions">
                <button 
                  class="btn btn-sm btn-primary" 
                  @click="viewCommande(commande)"
                  title="Voir les détails"
                >
                  <i class="fas fa-eye"></i>
                </button>
                <button 
                  v-if="commande.status === 'pending'"
                  class="btn btn-sm btn-success" 
                  @click="validateCommande(commande)"
                  title="Valider"
                >
                  <i class="fas fa-check"></i>
                </button>
                <button 
                  v-if="commande.status === 'pending'"
                  class="btn btn-sm btn-danger" 
                  @click="rejectCommande(commande)"
                  title="Rejeter"
                >
                  <i class="fas fa-times"></i>
                </button>
                <button 
                  class="btn btn-sm btn-outline" 
                  @click="editCommande(commande)"
                  title="Modifier"
                >
                  <i class="fas fa-edit"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="filteredCommandes.length === 0" class="empty-state">
        <div class="empty-icon">
          <i class="fas fa-shopping-cart"></i>
        </div>
        <h3>Aucune commande trouvée</h3>
        <p>{{ searchQuery ? 'Aucune commande ne correspond à votre recherche.' : 'Aucune commande n\'a été extraite pour le moment.' }}</p>
      </div>
    </div>

    <!-- Modal de détails -->
    <Teleport to="body">
    <div v-if="selectedCommande" class="modal-overlay" @click="closeModal">
      <div class="modal-content large" @click.stop>
        <div class="modal-header">
          <h2>
            <i class="fas fa-shopping-cart"></i>
            Commande #{{ selectedCommande.id }}
          </h2>
          <button class="modal-close" @click="closeModal" aria-label="Fermer">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="commande-details">
            <div class="details-section">
              <h3>Informations client</h3>
              <div class="info-grid">
                <div class="info-item">
                  <label>Nom:</label>
                  <span>{{ selectedCommande.clientName }}</span>
                </div>
                <div class="info-item">
                  <label>Email:</label>
                  <span>{{ selectedCommande.clientEmail || '—' }}</span>
                </div>
                <div class="info-item">
                  <label>Téléphone:</label>
                  <span>{{ selectedCommande.clientPhone || 'Non renseigné' }}</span>
                </div>
                <div class="info-item">
                  <label>Adresse:</label>
                  <span>{{ selectedCommande.clientAddress || 'Non renseignée' }}</span>
                </div>
              </div>
            </div>

            <div class="details-section">
              <h3>Détails de la commande</h3>
              <div class="info-grid">
                <div class="info-item">
                  <label>Date de création:</label>
                  <span>{{ formatDateTime(selectedCommande.createdAt) }}</span>
                </div>
                <div class="info-item">
                  <label>Statut:</label>
                  <span class="status-badge" :class="getStatusClass(selectedCommande.status)">
                    {{ getStatusText(selectedCommande.status) }}
                  </span>
                </div>
                <div class="info-item">
                  <label>Confiance IA:</label>
                  <span class="confidence-badge" :class="getConfidenceClass(selectedCommande.aiConfidence)">
                    {{ formatPercent(selectedCommande.aiConfidence) }}
                  </span>
                </div>
                <div class="info-item">
                  <label>Source:</label>
                  <span>{{ selectedCommande.source || 'Email' }}</span>
                </div>
              </div>
            </div>

            <div class="details-section">
              <h3>Articles commandés</h3>
              <div class="items-table table-responsive">
                <table>
                  <thead>
                    <tr>
                      <th>Produit</th>
                      <th>Référence</th>
                      <th>Quantité</th>
                      <th>Prix unitaire</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in selectedCommande.items" :key="item.id">
                      <td>{{ item.productName }}</td>
                      <td>{{ item.productRef }}</td>
                      <td>{{ formatCount(item.quantity) }}</td>
                      <td>{{ formatCurrency(item.unitPrice) }}</td>
                      <td>{{ formatCurrency(item.totalPrice) }}</td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr class="total-row">
                      <td colspan="4"><strong>Total</strong></td>
                      <td><strong>{{ formatCurrency(selectedCommande.totalAmount) }}</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">
            Fermer
          </button>
          <button 
            v-if="selectedCommande.status === 'pending'"
            type="button" 
            class="btn btn-danger" 
            @click="rejectCommande(selectedCommande)"
          >
            <i class="fas fa-times"></i>
            Rejeter
          </button>
          <button 
            v-if="selectedCommande.status === 'pending'"
            type="button" 
            class="btn btn-success" 
            @click="validateCommande(selectedCommande)"
          >
            <i class="fas fa-check"></i>
            Valider
          </button>
        </div>
      </div>
    </div>
    </Teleport>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useCommandesStore } from '../stores/commandes'

export default {
  name: 'Commandes',
  setup() {
    const commandesStore = useCommandesStore()
    
    const loading = ref(false)
    const searchQuery = ref('')
    const statusFilter = ref('')
    const selectedCommande = ref(null)
    
    const stats = computed(() => commandesStore.stats)
    const commandes = computed(() => commandesStore.commandes)
    
    const filteredCommandes = computed(() => {
      let filtered = commandes.value
      
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        filtered = filtered.filter(commande => 
          (commande.clientName || '').toLowerCase().includes(query) ||
          (commande.clientEmail || '').toLowerCase().includes(query) ||
          (commande.purchaseOrderNo || '').toLowerCase().includes(query) ||
          commande.id.toString().includes(query)
        )
      }
      
      if (statusFilter.value) {
        filtered = filtered.filter(commande => commande.status === statusFilter.value)
      }
      
      return filtered
    })
    
    const getStatusClass = (status) => {
      switch (status) {
        case 'DRAFT': return 'status-draft'
        case 'PROCESSING': return 'status-processing'
        case 'SENT_TO_SAP': return 'status-sent-to-sap'
        case 'PROCESSED': return 'status-processed'
        case 'PARTIALLY_CLOSED': return 'status-partially-closed'
        case 'CLOSED': return 'status-closed'
        case 'ANOMALY': return 'status-anomaly'
        default: return 'status-unknown'
      }
    }
    
    const getStatusText = (status) => {
      switch (status) {
        case 'DRAFT': return 'Commande en brouillon nécessitant validation'
        case 'PROCESSING': return 'Commande en cours de traitement'
        case 'SENT_TO_SAP': return 'Commande envoyée vers SAP'
        case 'PROCESSED': return 'Commande traitée avec succès'
        case 'PARTIALLY_CLOSED': return 'Commande partiellement clôturée'
        case 'CLOSED': return 'Commande entièrement clôturée'
        case 'ANOMALY': return 'Commande avec anomalie détectée'
        default: return 'Inconnu'
      }
    }
    
    const getConfidenceClass = (confidence) => {
      if (confidence === null || confidence === undefined) return ''
      if (confidence >= 80) return 'confidence-high'
      if (confidence >= 60) return 'confidence-medium'
      return 'confidence-low'
    }
    
    // « — » quand la valeur n'existe pas dans l'API (jamais de NaN / « Invalid Date »)
    const toDate = (date) => {
      if (!date) return null
      const d = new Date(date)
      return Number.isNaN(d.getTime()) ? null : d
    }
    
    const formatDate = (date) => {
      const d = toDate(date)
      return d ? d.toLocaleDateString('fr-FR') : '—'
    }
    
    const formatTime = (date) => {
      const d = toDate(date)
      return d ? d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''
    }
    
    const formatDateTime = (date) => {
      const d = toDate(date)
      return d ? d.toLocaleString('fr-FR') : '—'
    }
    
    const formatCurrency = (amount) => {
      if (amount === null || amount === undefined || amount === '') return '—'
      const value = Number(amount)
      if (!Number.isFinite(value)) return '—'
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR'
      }).format(value)
    }
    
    const formatCount = (value) => {
      if (value === null || value === undefined || value === '') return '—'
      const n = Number(value)
      return Number.isFinite(n) ? n.toLocaleString('fr-FR') : '—'
    }
    
    const formatPercent = (value) => {
      if (value === null || value === undefined || value === '') return '—'
      const n = Number(value)
      return Number.isFinite(n) ? `${n}%` : '—'
    }
    
    const refreshCommandes = async () => {
      loading.value = true
      try {
        await commandesStore.loadCommandes()
      } finally {
        loading.value = false
      }
    }
    
    const viewCommande = (commande) => {
      selectedCommande.value = commande
    }
    
    const closeModal = () => {
      selectedCommande.value = null
    }
    
    const validateCommande = async (commande) => {
      try {
        await commandesStore.validateCommande(commande.id)
        if (selectedCommande.value && selectedCommande.value.id === commande.id) {
          selectedCommande.value.status = 'validated'
        }
      } catch (error) {
        console.error('Erreur validation:', error)
      }
    }
    
    const rejectCommande = async (commande) => {
      try {
        await commandesStore.rejectCommande(commande.id)
        if (selectedCommande.value && selectedCommande.value.id === commande.id) {
          selectedCommande.value.status = 'rejected'
        }
      } catch (error) {
        console.error('Erreur rejet:', error)
      }
    }
    
    const editCommande = (commande) => {
      // TODO: Implémenter l'édition
      console.log('Éditer commande:', commande)
    }
    
    onMounted(() => {
      commandesStore.loadCommandes()
    })
    
    return {
      loading,
      searchQuery,
      statusFilter,
      selectedCommande,
      stats,
      commandes,
      filteredCommandes,
      getStatusClass,
      getStatusText,
      getConfidenceClass,
      formatDate,
      formatTime,
      formatDateTime,
      formatCurrency,
      formatCount,
      formatPercent,
      refreshCommandes,
      viewCommande,
      closeModal,
      validateCommande,
      rejectCommande,
      editCommande
    }
  }
}
</script>

<style scoped>
.commandes-page {
  padding: 2rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.header-content h1 {
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
}

.header-content h1 i {
  margin-right: 0.75rem;
  color: #6366f1;
}

.header-content p {
  color: #6b7280;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;
}

.search-box i {
  position: absolute;
  left: 1rem;
  color: #6b7280;
}

.search-input {
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  width: 300px;
}

.status-filter {
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  font-size: 1.25rem;
  color: white;
}

.stat-icon.draft {
  background: #f59e0b;
}

.stat-icon.processing {
  background: #3b82f6;
}

.stat-icon.processed {
  background: #10b981;
}

.stat-icon.anomaly {
  background: #ef4444;
}

.stat-icon.total {
  background: #6366f1;
}

.stat-content h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.25rem 0;
}

.stat-content p {
  color: #6b7280;
  margin: 0;
}

.commandes-table {
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.table-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.table-container {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th {
  background: #f9fafb;
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
}

.data-table td {
  padding: 1rem;
  border-bottom: 1px solid #f3f4f6;
}

.table-row:hover {
  background: #f9fafb;
}

.id-badge {
  background: #e0e7ff;
  color: #3730a3;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.875rem;
}

.client-name {
  font-weight: 600;
  color: #1f2937;
}

.client-email {
  color: #6b7280;
  font-size: 0.875rem;
}

.date {
  font-weight: 500;
  color: #1f2937;
}

.time {
  color: #6b7280;
  font-size: 0.875rem;
}

.currency {
  font-weight: 600;
  color: #059669;
}

.badge {
  background: #f3f4f6;
  color: #374151;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.875rem;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-draft {
  background: #fef3c7;
  color: #92400e;
}

.status-processing {
  background: #dbeafe;
  color: #1e40af;
}

.status-sent-to-sap {
  background: #e0e7ff;
  color: #3730a3;
}

.status-processed {
  background: #dcfce7;
  color: #166534;
}

.status-partially-closed {
  background: #fef3c7;
  color: #92400e;
}

.status-closed {
  background: #f3f4f6;
  color: #374151;
}

.status-anomaly {
  background: #fee2e2;
  color: #991b1b;
}

.confidence-bar {
  position: relative;
  width: 80px;
  height: 20px;
  background: #f3f4f6;
  border-radius: 10px;
  overflow: hidden;
}

.confidence-fill {
  height: 100%;
  border-radius: 10px;
  transition: width 0.3s ease;
}

.confidence-high {
  background: #10b981;
}

.confidence-medium {
  background: #f59e0b;
}

.confidence-low {
  background: #ef4444;
}

.confidence-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  border: none;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  text-decoration: none;
}

.btn i {
  margin-right: 0.5rem;
}

.btn-sm {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
}

.btn-sm i {
  margin-right: 0;
}

.btn-primary {
  background: #6366f1;
  color: white;
}

.btn-success {
  background: #10b981;
  color: white;
}

.btn-danger {
  background: #ef4444;
  color: white;
}

.btn-outline {
  background: white;
  color: #6b7280;
  border: 1px solid #d1d5db;
}

.btn-secondary {
  background: #6b7280;
  color: white;
}

.empty-state {
  text-align: center;
  padding: 4rem 2rem;
}

.empty-icon {
  width: 80px;
  height: 80px;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  font-size: 2rem;
  color: #9ca3af;
}

.empty-state h3 {
  font-size: 1.5rem;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.empty-state p {
  color: #6b7280;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1060;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-content.large {
  max-width: 1000px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h2 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  display: flex;
  align-items: center;
}

.modal-header h2 i {
  margin-right: 0.75rem;
  color: #6366f1;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.25rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0.5rem;
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.details-section {
  margin-bottom: 2rem;
}

.details-section h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 6px;
}

.info-item label {
  font-weight: 600;
  color: #374151;
}

.info-item span {
  color: #1f2937;
}

.items-table table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.items-table th,
.items-table td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid #f3f4f6;
}

.items-table th {
  background: #f9fafb;
  font-weight: 600;
  color: #374151;
}

.items-table .total-row {
  background: #f9fafb;
  font-weight: 600;
}

.confidence-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.875rem;
}

/* Robustesse : en-tête, textes longs, cellule actions */
.page-header {
  gap: 1rem;
}

.header-content {
  flex: 1 1 320px;
  min-width: 0;
  text-align: left;
}

.header-actions {
  flex-wrap: wrap;
  min-width: 0;
  max-width: 100%;
}

.status-filter {
  max-width: 100%;
  min-width: 0;
  text-overflow: ellipsis;
}

.table-header {
  flex-wrap: wrap;
  gap: 0.75rem;
}

.client-info {
  min-width: 0;
}

.client-name,
.client-email,
.info-item span,
.items-table td {
  overflow-wrap: anywhere;
}

.data-table td.actions {
  display: table-cell;
  white-space: nowrap;
}

.data-table td.actions .btn + .btn {
  margin-left: 0.5rem;
}

.info-grid {
  grid-template-columns: repeat(auto-fit, minmax(min(250px, 100%), 1fr));
}

.info-item {
  gap: 0.75rem;
  flex-wrap: wrap;
  min-width: 0;
}

.info-item span {
  text-align: right;
  min-width: 0;
}

.modal-header h2 {
  min-width: 0;
  overflow-wrap: anywhere;
}

.modal-footer {
  flex-wrap: wrap;
}

@media (max-width: 768px) {
  .header-actions {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-input {
    width: 100%;
  }
  
  .stats-cards {
    grid-template-columns: 1fr;
  }
  
  .table-container {
    overflow-x: scroll;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 991.98px) {
  .commandes-page {
    padding: 1rem 0;
  }
}

@media (max-width: 767.98px) {
  .page-header {
    margin-bottom: 1.25rem;
  }

  .header-content h1 {
    font-size: clamp(1.5rem, 6.5vw, 2rem);
  }

  .header-actions {
    width: 100%;
    gap: 0.75rem;
  }

  .search-box,
  .status-filter {
    width: 100%;
  }

  .stats-cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
    margin-bottom: 1.25rem;
  }

  .stat-card:last-child:nth-child(odd) {
    grid-column: 1 / -1;
  }

  .stat-card {
    padding: 1rem;
    min-width: 0;
  }

  .stat-icon {
    width: 40px;
    height: 40px;
    font-size: 1.1rem;
    margin-right: 0.75rem;
    flex-shrink: 0;
  }

  .stat-content {
    min-width: 0;
  }

  .stat-content h3 {
    font-size: 1.25rem;
  }

  .table-header {
    padding: 1rem;
  }

  /* Tableau -> cartes empilées sur téléphone */
  .table-container {
    overflow-x: visible;
  }

  .data-table,
  .data-table tbody,
  .data-table tr,
  .data-table td {
    display: block;
    width: 100%;
  }

  .data-table thead {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }

  .data-table tr {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .data-table td {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    padding: 0.4rem 0;
    border-bottom: none;
    text-align: right;
    min-width: 0;
  }

  .data-table td::before {
    content: attr(data-label);
    flex-shrink: 0;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #6b7280;
    text-align: left;
  }

  .data-table td.client-info {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    column-gap: 1rem;
  }

  .data-table td.client-info::before {
    grid-row: span 2;
    align-self: center;
  }

  .data-table td.date-info {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    column-gap: 1rem;
  }

  .data-table td.date-info::before {
    grid-row: span 2;
    align-self: center;
  }

  .data-table td .status-badge {
    min-width: 0;
    text-align: center;
  }

  .data-table td.actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 0.5rem;
    white-space: normal;
    padding-top: 0.75rem;
  }

  .data-table td.actions::before {
    margin-right: auto;
  }

  .data-table td.actions .btn + .btn {
    margin-left: 0;
  }

  /* Modale plein écran */
  .modal-overlay {
    align-items: stretch;
  }

  .modal-content,
  .modal-content.large {
    width: 100%;
    max-width: 100%;
    max-height: none;
    height: 100%;
    height: 100dvh;
    border-radius: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .modal-header,
  .modal-footer {
    padding: 1rem;
    flex-shrink: 0;
  }

  .modal-header h2 {
    font-size: 1.25rem;
  }

  .modal-body {
    padding: 1rem;
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .modal-footer .btn {
    flex: 1 1 auto;
    justify-content: center;
  }

  .items-table table {
    min-width: 520px;
  }
}

@media (max-width: 575.98px) {
  .commandes-page {
    padding: 0;
  }
}

@media (max-width: 359.98px) {
  .stats-cards {
    grid-template-columns: 1fr;
  }
}

@media (pointer: coarse) {
  .data-table td.actions .btn-sm,
  .modal-close {
    min-width: 44px;
    min-height: 44px;
    justify-content: center;
  }

  .modal-close {
    display: inline-flex;
    align-items: center;
  }
}
</style>